import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../api/client';
import { normaliseLanguage } from '../i18n';
import { themeForOrganisation } from '../theme';
import { SessionProvider, useSession } from './session';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      // Retrying a 4xx just delays the error message; a 5xx is worth one more attempt.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

/**
 * The active club's colour drives the theme, so the UI carries its branding.
 *
 * Before sign-in there is no club and `themeForOrganisation` returns the default aviation
 * palette, which is why one provider can serve both states.
 */
function BrandedTheme({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { organisation } = useSession();
  const theme = useMemo(
    () => themeForOrganisation(organisation?.primary_color),
    [organisation?.primary_color],
  );
  const datesLocale = normaliseLanguage(i18n.language) === 'sr' ? 'sr' : 'en-gb';

  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <DatesProvider settings={{ locale: datesLocale, firstDayOfWeek: 1, weekendDays: [0, 6] }}>
        <Notifications position="top-right" limit={4} />
        <ModalsProvider labels={{ confirm: t('action.confirm'), cancel: t('action.cancel') }}>
          {children}
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrandedTheme>{children}</BrandedTheme>
      </SessionProvider>
    </QueryClientProvider>
  );
}
